import { useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Button, Input } from "antd-mobile";
import useNavigation from "../../hooks/useNavigation";
import PageWrap from "../components/PageWrap";
import PageHeader, { headerHeight } from "../components/PageHeader";
import { useStores } from "../../models";
import { ToastError, ToastSuccess } from "../../components/Toast";

const verifiedSuccessImg = require("../../assets/images/wallet/verifiedSucces.png");

const Body = styled.div`
  height: calc(100% - ${headerHeight});
  overflow: auto;
  background: #f7f8ff;
`;

const Field = styled.div`
  display: flex;
  align-items: center;
  background: #fff;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
`;

const Label = styled.div`
  width: 72px;
  flex-shrink: 0;
  font-size: 15px;
  font-weight: 600;
  color: #333;
`;

const Tips = styled.div`
  padding: 20px;
  font-size: 14px;
  line-height: 1.7;
  color: rgb(127, 127, 127);
`;

const Footer = styled.div`
  padding: 20px 16px 32px;
  display: flex;
  justify-content: center;
`;

const SuccessBox = styled.div`
  min-height: calc(100% - ${headerHeight});
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
  background: #fff;
`;

const SuccessImg = styled.img`
  width: 150px;
  height: 150px;
  object-fit: contain;
`;

const SuccessTitle = styled.div`
  margin-top: 12px;
  font-size: 18px;
  font-weight: 600;
  color: #000;
`;

const SuccessTip = styled.div`
  margin-top: 12px;
  width: 80%;
  color: rgb(127, 127, 127);
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
`;

const city: Record<string, string> = {
  11: "北京",
  12: "天津",
  13: "河北",
  14: "山西",
  15: "内蒙古",
  21: "辽宁",
  22: "吉林",
  23: "黑龙江",
  31: "上海",
  32: "江苏",
  33: "浙江",
  34: "安徽",
  35: "福建",
  36: "江西",
  37: "山东",
  41: "河南",
  42: "湖北",
  43: "湖南",
  44: "广东",
  45: "广西",
  46: "海南",
  50: "重庆",
  51: "四川",
  52: "贵州",
  53: "云南",
  54: "西藏",
  61: "陕西",
  62: "甘肃",
  63: "青海",
  64: "宁夏",
  65: "新疆",
  71: "台湾",
  81: "香港",
  82: "澳门",
  91: "国外",
};

function identityIDCard(code: string) {
  const idCardReg =
    /^[1-9]\d{5}(19|20)?\d{2}(0[1-9]|1[012])(0[1-9]|[12]\d|3[01])\d{3}(\d|X)$/i;
  let errorMessage = "";
  let isPass = true;
  if (!code) {
    errorMessage = "请输入身份证号码";
    isPass = false;
  } else if (!code.match(idCardReg)) {
    errorMessage = "请输入正确的身份证号码";
    isPass = false;
  } else if (!city[code.substr(0, 2)]) {
    errorMessage = "请输入正确的身份证号码";
    isPass = false;
  } else if (code.length === 18) {
    const chars = code.split("");
    const factor = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
    const parity = [1, 0, "X", 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < 17; i++) sum += parseInt(chars[i], 10) * factor[i];
    const last = parity[sum % 11];
    if (String(last) !== chars[17]) {
      errorMessage = "请输入正确的身份证号码";
      isPass = false;
    }
  }
  return { errorMessage, isPass };
}

const isAllChinese = (str: string) => /^[\u4E00-\u9FA5]+$/.test(str);

const VerifiedSuccessView = ({
  realName,
  onDone,
}: {
  realName?: string | null;
  onDone: () => void;
}) => (
  <SuccessBox>
    <SuccessImg src={verifiedSuccessImg} alt="" />
    <SuccessTitle>已认证：{realName || ""}</SuccessTitle>
    <SuccessTip>
      为了您的交易安全，实名后不可更改请绑定实名注册的账号或卡号
    </SuccessTip>
    <div style={{ marginTop: 20, width: "80%" }}>
      <Button
        block
        style={{ background: "rgba(0,0,0,0.8)", color: "#fff", border: 0 }}
        onClick={onDone}
      >
        认证成功
      </Button>
    </div>
  </SuccessBox>
);

const Verified = () => {
  const { navigate } = useNavigation();
  const { apiStore, authStore, walletStore } = useStores();
  const [realName, setRealName] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [loading, setLoading] = useState(false);

  if (walletStore.is_id_verify) {
    return (
      <PageWrap>
        <PageHeader title="实名认证" />
        <VerifiedSuccessView
          realName={walletStore.realName}
          onDone={() => navigate("Wallet")}
        />
      </PageWrap>
    );
  }

  const verify = async () => {
    if (!isAllChinese(realName)) return ToastError("请输入正确姓名");
    const check = identityIDCard(idNumber.trim().toUpperCase());
    if (check.errorMessage) return ToastError(check.errorMessage);
    try {
      setLoading(true);
      const res = await apiStore.verify({
        token: authStore.getToken,
        real_name: realName,
        id_number: idNumber.trim().toUpperCase(),
      });
      if (res?.code === 200 || res?.code === 0) {
        walletStore.setVerify?.();
        await walletStore.getWalletInfo();
        ToastSuccess("认证成功");
        navigate("VerifiedSuccess");
      } else {
        ToastError((res as any)?.message || "认证失败");
      }
    } catch (e: any) {
      ToastError(e.message || "认证失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrap>
      <PageHeader title="实名认证" />
      <Body>
        <Field>
          <Label>姓名</Label>
          <Input
            placeholder="请输入您的真实姓名"
            value={realName}
            onChange={setRealName}
            clearable
            style={{ "--font-size": "15px" } as any}
          />
        </Field>
        <Field>
          <Label>身份证</Label>
          <Input
            placeholder="请输入您的身份证"
            value={idNumber}
            onChange={setIdNumber}
            clearable
            maxLength={18}
            style={{ "--font-size": "15px" } as any}
          />
        </Field>
        <Tips>
          <div>1、实名需与身份证一致，否则无法通过校验；</div>
          <div>2、身份认证后，只能绑定该身份证办理的银行卡</div>
        </Tips>
        <Footer>
          <Button
            color="primary"
            loading={loading}
            onClick={verify}
            style={{ width: "90%", maxWidth: 360 }}
          >
            确认
          </Button>
        </Footer>
      </Body>
    </PageWrap>
  );
};

export default observer(Verified);
